import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useProfile } from "./ProfileContext";
import { useIntelligenceScope } from "@/hooks/queries/use-intelligence-scope";

/** localStorage key prefix for the user's active team, scoped per business. */
const STORAGE_PREFIX = "graftus_activeTeamId_";

interface TeamScopeContextValue {
  /** The team the user is currently focused on. `null` means "All teams" (org-wide). */
  activeTeamId: string | null;
  setActiveTeamId: (id: string | null) => void;
}

const TeamScopeContext = createContext<TeamScopeContextValue | undefined>(
  undefined,
);

/**
 * Holds the single "active team" the user is viewing. Drives both the sidebar
 * TeamScopeSwitcher and the team-scoped pages (Pulse/Tests/Intelligence default
 * scope). Persisted per business; team-scoped users (no org-wide access) are
 * seeded to their first visible team so they always land inside their subtree.
 */
export function TeamScopeProvider({ children }: { children: ReactNode }) {
  const { businessProfile, memberAccessLevel } = useProfile();
  const businessId = businessProfile?.uid ?? "";
  // Report-only accounts (GRA-96) are refused by getMyScope; skip the call.
  const scopeQuery = useIntelligenceScope(
    memberAccessLevel === "none" ? undefined : businessId || undefined,
  );

  const [activeTeamId, setActiveTeamIdState] = useState<string | null>(null);
  // Tracks which business the current state was hydrated for, so a business
  // switch re-reads from storage exactly once.
  const [hydratedBusinessId, setHydratedBusinessId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (!businessId) {
      setActiveTeamIdState(null);
      setHydratedBusinessId(null);
      return;
    }
    if (hydratedBusinessId === businessId) return;
    const stored = localStorage.getItem(STORAGE_PREFIX + businessId);
    setActiveTeamIdState(stored && stored.length ? stored : null);
    setHydratedBusinessId(businessId);
  }, [businessId, hydratedBusinessId]);

  // Reconcile the active team against the user's current visibility once it
  // loads. Two cases for a team-scoped user:
  //   1. No (or an invalid) selection → seed to their first visible team.
  //   2. A previously stored team that is no longer visible — access was
  //      narrowed, or the team was removed — → snap back to a visible team.
  // Org-wide users may view any team (or "All teams"), so their selection is
  // left untouched. The correction is persisted so a stale id can't linger in
  // storage and resurface on the next load.
  useEffect(() => {
    if (!businessId || hydratedBusinessId !== businessId) return;
    const data = scopeQuery.data;
    if (!data || data.mode !== "scoped") return;
    const visible = data.visibleTeamIds;
    if (visible.length === 0) return;
    if (activeTeamId && visible.includes(activeTeamId)) return;
    const next = visible[0];
    setActiveTeamIdState(next);
    localStorage.setItem(STORAGE_PREFIX + businessId, next);
  }, [businessId, hydratedBusinessId, scopeQuery.data, activeTeamId]);

  const setActiveTeamId = useCallback(
    (id: string | null) => {
      setActiveTeamIdState(id);
      if (!businessId) return;
      if (id) localStorage.setItem(STORAGE_PREFIX + businessId, id);
      else localStorage.removeItem(STORAGE_PREFIX + businessId);
    },
    [businessId],
  );

  return (
    <TeamScopeContext.Provider value={{ activeTeamId, setActiveTeamId }}>
      {children}
    </TeamScopeContext.Provider>
  );
}

export function useTeamScope(): TeamScopeContextValue {
  const ctx = useContext(TeamScopeContext);
  if (!ctx) {
    throw new Error("useTeamScope must be used within a TeamScopeProvider");
  }
  return ctx;
}
